import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { GridItem, PlayerWithLinkColumn, PlayerWithoutLinkColumn } from "../common-columns";
import { eventDateFormatter, percentFormatter, velocityFormatter } from "../formatters";

/**
 * Builds the Pitching → Breakdown grid rows. Every breakdown stat (#P, the per-pitch-type counts,
 * strike/swing rates, and MPH averages) already arrives on `defense` under the exact keys the
 * breakdown columns reference, so the stats spread straight through; we only attach the pinned
 * player cell. Rows without a defense block (non-pitchers) are dropped.
 */
export const pitchingBreakdownGridDataFormatted = (grid: GridItem[]) => {
  return grid
    .filter(({ defense }) => defense)
    .map(({ person_id, team_id, name, eventID, date, defense }) => ({
      player: { id: person_id, teamID: team_id, name, eventID, status: "active" },
      // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
      event: date,
      ...defense,
    }));
};

export const CorePitchingBreakdown: StatsInfo[] = [
  { name: "FB", field: "FB", def: "Number of pitches thrown as Fastballs" },
  { name: "FBS", field: "FBS", def: "Number of Fastballs thrown for strikes" },
  {
    name: "FBS%",
    field: "FBS%",
    def: "Percentage of Fastballs thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "FBSW%",
    field: "FBSW%",
    def: "Percentage of Fastballs swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "FBSM%",
    field: "FBSM%",
    def: "Percentage of Fastballs swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHFB",
    field: "MPHFB",
    def: "Fastball average velocity",
    valueFormatter: velocityFormatter,
  },
  { name: "CT", field: "CT", def: "Number of pitches thrown as Cutters" },
  { name: "CTS", field: "CTS", def: "Number of Cutters thrown for strikes" },
  {
    name: "CTS%",
    field: "CTS%",
    def: "Percentage of Cutters thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "CTSW%",
    field: "CTSW%",
    def: "Percentage of Cutters swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "CTSM%",
    field: "CTSM%",
    def: "Percentage of Cutters swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHCT",
    field: "MPHCT",
    def: "Cutter average velocity",
    valueFormatter: velocityFormatter,
  },
  { name: "CB", field: "CB", def: "Number of pitches thrown as Curveballs" },
  { name: "CBS", field: "CBS", def: "Number of Curveballs thrown for strikes" },
  {
    name: "CBS%",
    field: "CBS%",
    def: "Percentage of Curveballs thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "CBSW%",
    field: "CBSW%",
    def: "Percentage of Curveballs swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "CBSM%",
    field: "CBSM%",
    def: "Percentage of Curveballs swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHCB",
    field: "MPHCB",
    def: "Curveball average velocity",
    valueFormatter: velocityFormatter,
  },
  { name: "SL", field: "SL", def: "Number of pitches thrown as Sliders" },
  { name: "SLS", field: "SLS", def: "Number of Sliders thrown for strikes" },
  {
    name: "SLS%",
    field: "SLS%",
    def: "Percentage of Sliders thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "SLSW%",
    field: "SLSW%",
    def: "Percentage of Sliders swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "SLSM%",
    field: "SLSM%",
    def: "Percentage of Sliders swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHSL",
    field: "MPHSL",
    def: "Slider average velocity",
    valueFormatter: velocityFormatter,
  },
  { name: "CH", field: "CH", def: "Number of pitches thrown as Changeups" },
  { name: "CHS", field: "CHS", def: "Number of Changeups thrown for strikes" },
  {
    name: "CHS%",
    field: "CHS%",
    def: "Percentage of Changeups thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "CHSW%",
    field: "CHSW%",
    def: "Percentage of Changeups swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "CHSM%",
    field: "CHSM%",
    def: "Percentage of Changeups swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHCH",
    field: "MPHCH",
    def: "Changeup average velocity",
    valueFormatter: velocityFormatter,
  },
  { name: "OS", field: "OS", def: "Number of pitches thrown as Offspeeds" },
  { name: "OSS", field: "OSS", def: "Number of Offspeeds thrown for strikes" },
  {
    name: "OSS%",
    field: "OSS%",
    def: "Percentage of Offspeeds thrown for strikes",
    valueFormatter: percentFormatter,
  },
  {
    name: "OSSW%",
    field: "OSSW%",
    def: "Percentage of Offspeeds swung at",
    valueFormatter: percentFormatter,
  },
  {
    name: "OSSM%",
    field: "OSSM%",
    def: "Percentage of Offspeeds swung at and missed",
    valueFormatter: percentFormatter,
  },
  {
    name: "MPHOS",
    field: "MPHOS",
    def: "Offspeed average velocity",
    valueFormatter: velocityFormatter,
  },
];

export const PitchingBreakdown: StatsInfo[] = (
  [
    PlayerWithLinkColumn,
    { name: "#P", field: "#P", def: "Total pitches", columnProps: { sort: "desc" } },
  ] as StatsInfo[]
).concat(CorePitchingBreakdown);

export const EventPitchingBreakdown: StatsInfo[] = (
  [
    {
      name: "Date",
      field: "event",
      columnProps: {
        width: 100,
        pinned: true,
        cellStyle: { textAlign: "left" },
        sort: "asc",
        valueFormatter: eventDateFormatter,
      },
    },
    {
      name: "OPPONENT",
      field: "opponent",
      columnProps: {
        width: 260,
        cellStyle: { textAlign: "left" },
        headerClass: "left-aligned-column-header",
      },
    },
    { name: "#P", field: "#P", def: "Total pitches" },
  ] as StatsInfo[]
).concat(CorePitchingBreakdown);

export const GamePitchingBreakdown = [PlayerWithoutLinkColumn, ...PitchingBreakdown.slice(1)];
